import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import emailjs from '@emailjs/browser';
import { 
  ArrowRight, 
  ArrowLeft,
  Check,
  Monitor,
  ShoppingCart,
  Target,
  Sparkles,
  Palette,
  Type,
  Layers,
  Send,
  Building2,
  Briefcase,
  Utensils,
  Heart,
  Car,
  Home,
  Scissors,
  Dumbbell,
  GraduationCap,
  Music,
  Camera,
  Zap,
  MousePointer,
  Globe,
  Search,
  MessageSquare,
  Calendar,
  Users,
  ShoppingBag,
  CreditCard,
  BarChart3,
  Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// EmailJS configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_BUILDER_TEMPLATE_ID;
const EMAILJS_AUTOREPLY_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// Step Data
const STEPS = [
  { id: 1, title: "Projekt típus", subtitle: "Mit szeretnél készíttetni?" },
  { id: 2, title: "Iparág", subtitle: "Milyen területen működsz?" },
  { id: 3, title: "Stílus", subtitle: "Milyen hangulatot szeretnél?" },
  { id: 4, title: "Színek", subtitle: "Válaszd ki a színvilágot" },
  { id: 5, title: "Betűtípus", subtitle: "Milyen karaktert sugározzon?" },
  { id: 6, title: "Animációk", subtitle: "Mennyire legyen élénk?" },
  { id: 7, title: "Funkciók", subtitle: "Mire van szükséged?" },
  { id: 8, title: "Összegzés", subtitle: "Ellenőrizd és küldd el" },
];

const PROJECT_TYPES = [
  { id: "weboldal", label: "Céges weboldal", desc: "Bemutatkozó, szolgáltatói oldal", icon: Monitor },
  { id: "landing", label: "Landing Page", desc: "Egyoldalas, kampány céloldal", icon: Target },
  { id: "webshop", label: "Webshop", desc: "Online áruház, e-commerce", icon: ShoppingCart },
];

const INDUSTRIES = [
  { id: "epitoipar", label: "Építőipar", icon: Building2 },
  { id: "szolgaltatas", label: "Szolgáltatás", icon: Briefcase },
  { id: "vendeglatas", label: "Vendéglátás", icon: Utensils },
  { id: "egeszseg", label: "Egészség & Szépség", icon: Heart },
  { id: "auto", label: "Autó & Szállítás", icon: Car },
  { id: "ingatlan", label: "Ingatlan", icon: Home },
  { id: "fodrasz", label: "Fodrászat & Kozmetika", icon: Scissors },
  { id: "fitness", label: "Fitness & Sport", icon: Dumbbell },
  { id: "oktatas", label: "Oktatás", icon: GraduationCap },
  { id: "kreativ", label: "Kreatív & Művészet", icon: Music },
  { id: "foto", label: "Fotó & Videó", icon: Camera },
  { id: "egyeb", label: "Egyéb", icon: Sparkles },
];

const STYLES = [
  { id: "minimal", label: "Minimalista", desc: "Tiszta, letisztult, kevesebb több", preview: "bg-zinc-900" },
  { id: "elegant", label: "Elegáns", desc: "Prémium, luxus érzet", preview: "bg-gradient-to-br from-zinc-900 to-amber-950" },
  { id: "bold", label: "Merész", desc: "Erős színek, feltűnő", preview: "bg-gradient-to-br from-purple-900 to-pink-900" },
  { id: "modern", label: "Modern", desc: "Trendi, kortárs dizájn", preview: "bg-gradient-to-br from-blue-900 to-cyan-900" },
  { id: "classic", label: "Klasszikus", desc: "Időtálló, hagyományos", preview: "bg-gradient-to-br from-stone-800 to-stone-900" },
  { id: "playful", label: "Játékos", desc: "Vidám, barátságos", preview: "bg-gradient-to-br from-orange-900 to-yellow-900" },
];

const COLOR_SCHEMES = [
  { id: "dark-gold", label: "Fekete & Arany", colors: ["#030303", "#D4AF37", "#FFFFFF"], desc: "Prémium, elegáns" },
  { id: "dark-blue", label: "Sötét & Kék", colors: ["#0a0a1a", "#3B82F6", "#FFFFFF"], desc: "Professzionális" },
  { id: "dark-green", label: "Sötét & Zöld", colors: ["#0a1a0a", "#22C55E", "#FFFFFF"], desc: "Természetes, friss" },
  { id: "dark-red", label: "Sötét & Vörös", colors: ["#1a0a0a", "#EF4444", "#FFFFFF"], desc: "Erőteljes, szenvedélyes" },
  { id: "light-blue", label: "Világos & Kék", colors: ["#F8FAFC", "#2563EB", "#1E293B"], desc: "Megbízható, tiszta" },
  { id: "light-warm", label: "Világos & Meleg", colors: ["#FFFBEB", "#D97706", "#1C1917"], desc: "Barátságos, meleg" },
  { id: "mono", label: "Monokróm", colors: ["#FFFFFF", "#000000", "#6B7280"], desc: "Időtlen, letisztult" },
  { id: "custom", label: "Egyedi színek", colors: ["#???", "#???", "#???"], desc: "Saját márkaszínek" },
];

const FONTS = [
  { id: "elegant-serif", label: "Elegáns Serif", family: "Cormorant Garamond", desc: "Kifinomult, prémium", style: "font-['Cormorant_Garamond']" },
  { id: "modern-sans", label: "Modern Sans", family: "Inter", desc: "Tiszta, olvasható", style: "font-['Inter']" },
  { id: "bold-display", label: "Merész Display", family: "Manrope", desc: "Erőteljes, figyelemfelkeltő", style: "font-['Manrope']" },
  { id: "friendly", label: "Barátságos", family: "Nunito", desc: "Lekerekített, kedves", style: "font-['Nunito']" },
  { id: "tech", label: "Tech / Startup", family: "Space Grotesk", desc: "Innovatív, modern", style: "font-['Space_Grotesk']" },
  { id: "classic-serif", label: "Klasszikus Serif", family: "Playfair Display", desc: "Hagyományos, elegáns", style: "font-['Playfair_Display']" },
];

const ANIMATIONS = [
  { id: "none", label: "Nincs animáció", desc: "Statikus, gyors betöltés", intensity: 0 },
  { id: "subtle", label: "Finom", desc: "Enyhe fade-in effektek", intensity: 1 },
  { id: "moderate", label: "Mérsékelt", desc: "Elegáns megjelenések", intensity: 2 },
  { id: "dynamic", label: "Dinamikus", desc: "Feltűnő, modern animációk", intensity: 3 },
];

const FEATURES = [
  { id: "contact-form", label: "Kapcsolati űrlap", icon: MessageSquare, category: "alap" },
  { id: "booking", label: "Időpontfoglalás", icon: Calendar, category: "alap" },
  { id: "gallery", label: "Galéria", icon: Camera, category: "alap" },
  { id: "testimonials", label: "Vélemények szekció", icon: Users, category: "alap" },
  { id: "blog", label: "Blog / Hírek", icon: Globe, category: "extra" },
  { id: "seo", label: "SEO optimalizálás", icon: Search, category: "extra" },
  { id: "multilang", label: "Többnyelvűség", icon: Globe, category: "extra" },
  { id: "analytics", label: "Analitika integráció", icon: BarChart3, category: "extra" },
  { id: "cart", label: "Kosár funkció", icon: ShoppingBag, category: "webshop" },
  { id: "payment", label: "Online fizetés", icon: CreditCard, category: "webshop" },
  { id: "inventory", label: "Készletkezelés", icon: Layers, category: "webshop" },
  { id: "admin", label: "Admin felület", icon: Lock, category: "webshop" },
];

// Progress Bar Component
const ProgressBar = ({ currentStep, totalSteps }) => {
  const progress = ((currentStep) / totalSteps) * 100;
  
  return (
    <div className="mb-8 sm:mb-12">
      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <span className="text-zinc-500 text-xs sm:text-sm">{currentStep}. lépés / {totalSteps}</span>
        <span className="text-[#D4AF37] text-xs sm:text-sm font-medium">{Math.round(progress)}%</span>
      </div>
      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
        <motion.div 
          className="h-full bg-[#D4AF37]"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
    </div>
  );
};

// Selection Card Component
const SelectionCard = ({ item, isSelected, onClick, children }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`relative p-4 sm:p-6 text-left rounded-lg border transition-all duration-300 ${
      isSelected 
        ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
        : 'border-white/10 bg-white/[0.02] hover:border-white/20'
    }`}
  >
    {isSelected && (
      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 sm:w-6 sm:h-6 bg-[#D4AF37] rounded-full flex items-center justify-center">
        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-black" strokeWidth={2} />
      </div>
    )}
    {children}
  </motion.button>
);

// Step 1: Project Type
const Step1ProjectType = ({ value, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
    {PROJECT_TYPES.map((type) => (
      <SelectionCard
        key={type.id}
        item={type}
        isSelected={value === type.id}
        onClick={() => onChange(type.id)}
      >
        <type.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#D4AF37] mb-3 sm:mb-4" strokeWidth={1.5} />
        <h3 className="text-lg sm:text-xl font-['Cormorant_Garamond'] text-white mb-1 sm:mb-2">{type.label}</h3>
        <p className="text-zinc-500 text-xs sm:text-sm">{type.desc}</p>
      </SelectionCard>
    ))}
  </div>
);

// Step 2: Industry
const Step2Industry = ({ value, onChange }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-4">
    {INDUSTRIES.map((industry) => (
      <SelectionCard
        key={industry.id}
        item={industry}
        isSelected={value === industry.id}
        onClick={() => onChange(industry.id)}
      >
        <industry.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37] mb-2 sm:mb-3" strokeWidth={1.5} />
        <h3 className="text-white text-xs sm:text-sm font-medium">{industry.label}</h3>
      </SelectionCard>
    ))}
  </div>
);

// Step 3: Style
const Step3Style = ({ value, onChange }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
    {STYLES.map((style) => (
      <SelectionCard
        key={style.id}
        item={style}
        isSelected={value === style.id}
        onClick={() => onChange(style.id)}
      >
        <div className={`w-full h-16 sm:h-24 rounded-md mb-3 sm:mb-4 ${style.preview}`} />
        <h3 className="text-base sm:text-lg font-['Cormorant_Garamond'] text-white mb-0.5 sm:mb-1">{style.label}</h3>
        <p className="text-zinc-500 text-xs sm:text-sm">{style.desc}</p>
      </SelectionCard>
    ))}
  </div>
);

// Step 4: Colors
const Step4Colors = ({ value, onChange }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
    {COLOR_SCHEMES.map((scheme) => (
      <SelectionCard
        key={scheme.id}
        item={scheme}
        isSelected={value === scheme.id}
        onClick={() => onChange(scheme.id)}
      >
        <div className="flex gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {scheme.colors.map((color, idx) => (
            <div 
              key={idx}
              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-white/20"
              style={{ backgroundColor: color.startsWith('#?') ? '#333' : color }}
            />
          ))}
        </div>
        <h3 className="text-white text-xs sm:text-sm font-medium mb-0.5 sm:mb-1">{scheme.label}</h3>
        <p className="text-zinc-500 text-[0.65rem] sm:text-xs">{scheme.desc}</p>
      </SelectionCard>
    ))}
  </div>
);

// Step 5: Fonts
const Step5Fonts = ({ value, onChange }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
    {FONTS.map((font) => (
      <SelectionCard
        key={font.id}
        item={font}
        isSelected={value === font.id}
        onClick={() => onChange(font.id)}
      >
        <div className={`text-2xl sm:text-3xl text-white mb-2 sm:mb-3 ${font.style}`}>Aa Bb</div>
        <h3 className="text-white text-xs sm:text-sm font-medium mb-0.5 sm:mb-1">{font.label}</h3>
        <p className="text-zinc-500 text-[0.65rem] sm:text-xs">{font.desc}</p>
        <p className="text-zinc-600 text-[0.6rem] sm:text-xs mt-1 sm:mt-2">{font.family}</p>
      </SelectionCard>
    ))}
  </div>
);

// Step 6: Animations
const Step6Animations = ({ value, onChange }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
    {ANIMATIONS.map((anim) => (
      <SelectionCard
        key={anim.id}
        item={anim}
        isSelected={value === anim.id}
        onClick={() => onChange(anim.id)}
      >
        <div className="flex gap-1 mb-3 sm:mb-4">
          {[...Array(4)].map((_, idx) => (
            <div 
              key={idx}
              className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${
                idx < anim.intensity ? 'bg-[#D4AF37]' : 'bg-white/10'
              }`}
            />
          ))}
        </div>
        <h3 className="text-white text-xs sm:text-sm font-medium mb-0.5 sm:mb-1">{anim.label}</h3>
        <p className="text-zinc-500 text-[0.65rem] sm:text-xs">{anim.desc}</p>
      </SelectionCard>
    ))}
  </div>
);

// Step 7: Features
const Step7Features = ({ value, onChange, projectType }) => {
  const toggleFeature = (featureId) => {
    if (value.includes(featureId)) {
      onChange(value.filter(id => id !== featureId));
    } else {
      onChange([...value, featureId]);
    }
  };

  const filteredFeatures = FEATURES.filter(f => {
    if (projectType === 'webshop') return true;
    return f.category !== 'webshop';
  });

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h4 className="text-zinc-400 text-xs uppercase tracking-wider mb-3 sm:mb-4">Alap funkciók</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          {filteredFeatures.filter(f => f.category === 'alap').map((feature) => (
            <SelectionCard
              key={feature.id}
              item={feature}
              isSelected={value.includes(feature.id)}
              onClick={() => toggleFeature(feature.id)}
            >
              <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] mb-1.5 sm:mb-2" strokeWidth={1.5} />
              <h3 className="text-white text-xs sm:text-sm">{feature.label}</h3>
            </SelectionCard>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-zinc-400 text-xs uppercase tracking-wider mb-3 sm:mb-4">Extra funkciók</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          {filteredFeatures.filter(f => f.category === 'extra').map((feature) => (
            <SelectionCard
              key={feature.id}
              item={feature}
              isSelected={value.includes(feature.id)}
              onClick={() => toggleFeature(feature.id)}
            >
              <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] mb-1.5 sm:mb-2" strokeWidth={1.5} />
              <h3 className="text-white text-xs sm:text-sm">{feature.label}</h3>
            </SelectionCard>
          ))}
        </div>
      </div>

      {projectType === 'webshop' && (
        <div>
          <h4 className="text-zinc-400 text-xs uppercase tracking-wider mb-3 sm:mb-4">Webshop funkciók</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {filteredFeatures.filter(f => f.category === 'webshop').map((feature) => (
              <SelectionCard
                key={feature.id}
                item={feature}
                isSelected={value.includes(feature.id)}
                onClick={() => toggleFeature(feature.id)}
              >
                <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] mb-1.5 sm:mb-2" strokeWidth={1.5} />
                <h3 className="text-white text-xs sm:text-sm">{feature.label}</h3>
              </SelectionCard>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Step 8: Summary
const Step8Summary = ({ config, contactInfo, setContactInfo }) => {
  const getLabel = (array, id) => array.find(item => item.id === id)?.label || id;
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
      {/* Summary */}
      <div>
        <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl text-white mb-4 sm:mb-6">Az összeállításod</h3>
        <div className="space-y-3 sm:space-y-4">
          <div className="flex justify-between py-2 sm:py-3 border-b border-white/10">
            <span className="text-zinc-500 text-sm">Projekt típus</span>
            <span className="text-white text-sm">{getLabel(PROJECT_TYPES, config.projectType)}</span>
          </div>
          <div className="flex justify-between py-2 sm:py-3 border-b border-white/10">
            <span className="text-zinc-500 text-sm">Iparág</span>
            <span className="text-white text-sm">{getLabel(INDUSTRIES, config.industry)}</span>
          </div>
          <div className="flex justify-between py-2 sm:py-3 border-b border-white/10">
            <span className="text-zinc-500 text-sm">Stílus</span>
            <span className="text-white text-sm">{getLabel(STYLES, config.style)}</span>
          </div>
          <div className="flex justify-between py-2 sm:py-3 border-b border-white/10">
            <span className="text-zinc-500 text-sm">Színvilág</span>
            <span className="text-white text-sm">{getLabel(COLOR_SCHEMES, config.colors)}</span>
          </div>
          <div className="flex justify-between py-2 sm:py-3 border-b border-white/10">
            <span className="text-zinc-500 text-sm">Betűtípus</span>
            <span className="text-white text-sm">{getLabel(FONTS, config.font)}</span>
          </div>
          <div className="flex justify-between py-2 sm:py-3 border-b border-white/10">
            <span className="text-zinc-500 text-sm">Animációk</span>
            <span className="text-white text-sm">{getLabel(ANIMATIONS, config.animations)}</span>
          </div>
          <div className="py-2 sm:py-3">
            <span className="text-zinc-500 block mb-2 text-sm">Funkciók</span>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {config.features.map(featureId => (
                <span key={featureId} className="px-2 sm:px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[0.65rem] sm:text-xs rounded">
                  {getLabel(FEATURES, featureId)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div>
        <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl text-white mb-4 sm:mb-6">Elérhetőséged</h3>
        <div className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-zinc-500 text-[0.65rem] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">Név *</label>
            <Input
              value={contactInfo.name}
              onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
              className="bg-white/[0.02] border-white/10 h-10 sm:h-12 text-white text-sm"
              placeholder="Teljes neved"
            />
          </div>
          <div>
            <label className="block text-zinc-500 text-[0.65rem] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">E-mail *</label>
            <Input
              type="email"
              value={contactInfo.email}
              onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
              className="bg-white/[0.02] border-white/10 h-10 sm:h-12 text-white text-sm"
              placeholder="email@ceg.hu"
            />
          </div>
          <div>
            <label className="block text-zinc-500 text-[0.65rem] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">Telefon</label>
            <Input
              value={contactInfo.phone}
              onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
              className="bg-white/[0.02] border-white/10 h-10 sm:h-12 text-white text-sm"
              placeholder="+36 30 123 4567"
            />
          </div>
          <div>
            <label className="block text-zinc-500 text-[0.65rem] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">Megjegyzés</label>
            <Textarea
              value={contactInfo.notes}
              onChange={(e) => setContactInfo({ ...contactInfo, notes: e.target.value })}
              className="bg-white/[0.02] border-white/10 text-white text-sm min-h-[80px] sm:min-h-[100px] resize-none"
              placeholder="Bármi extra, amit tudnunk kell..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Builder Page
export default function BuilderPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [config, setConfig] = useState({
    projectType: "",
    industry: "",
    style: "",
    colors: "",
    font: "",
    animations: "",
    features: [],
  });

  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return config.projectType !== "";
      case 2: return config.industry !== "";
      case 3: return config.style !== "";
      case 4: return config.colors !== "";
      case 5: return config.font !== "";
      case 6: return config.animations !== "";
      case 7: return config.features.length > 0;
      case 8: return contactInfo.name !== "" && contactInfo.email !== "";
      default: return true;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    
    setIsSubmitting(true);
    
    // Get labels for config values
    const getLabel = (array, id) => array.find(item => item.id === id)?.label || id;
    const featureLabels = config.features.map(id => getLabel(FEATURES, id)).join(", ");
    
    const templateParams = {
      name: contactInfo.name,
      email: contactInfo.email,
      phone: contactInfo.phone || "Nincs megadva",
      title: "Konfigurátor - " + getLabel(PROJECT_TYPES, config.projectType),
      message: `
WEBOLDAL KONFIGURÁCIÓ

Név: ${contactInfo.name}
Email: ${contactInfo.email}
Telefon: ${contactInfo.phone || "Nincs megadva"}

Projekt típus: ${getLabel(PROJECT_TYPES, config.projectType)}
Iparág: ${getLabel(INDUSTRIES, config.industry)}
Stílus: ${getLabel(STYLES, config.style)}
Színvilág: ${getLabel(COLOR_SCHEMES, config.colors)}
Betűtípus: ${getLabel(FONTS, config.font)}
Animációk: ${getLabel(ANIMATIONS, config.animations)}
Funkciók: ${featureLabels}

Megjegyzés: ${contactInfo.notes || "Nincs"}
      `.trim(),
    };

    try {
      // Send notification email to site owner
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setIsSubmitted(true);
      toast.success("Sikeresen elküldve! Hamarosan jelentkezem.");
    } catch (error) {
      console.error("EmailJS error:", error);
      toast.error("Hiba történt. Kérlek próbáld újra.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center px-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg"
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#D4AF37]/10 border border-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
            <Check className="w-8 h-8 sm:w-10 sm:h-10 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl text-white mb-3 sm:mb-4">
            Köszönöm!
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base mb-6 sm:mb-8">
            Megkaptam az összeállításodat. 24 órán belül jelentkezem egy személyre szabott ajánlattal.
          </p>
          <Link to="/">
            <Button className="btn-gold">
              Vissza a főoldalra
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1ProjectType value={config.projectType} onChange={(v) => updateConfig('projectType', v)} />;
      case 2: return <Step2Industry value={config.industry} onChange={(v) => updateConfig('industry', v)} />;
      case 3: return <Step3Style value={config.style} onChange={(v) => updateConfig('style', v)} />;
      case 4: return <Step4Colors value={config.colors} onChange={(v) => updateConfig('colors', v)} />;
      case 5: return <Step5Fonts value={config.font} onChange={(v) => updateConfig('font', v)} />;
      case 6: return <Step6Animations value={config.animations} onChange={(v) => updateConfig('animations', v)} />;
      case 7: return <Step7Features value={config.features} onChange={(v) => updateConfig('features', v)} projectType={config.projectType} />;
      case 8: return <Step8Summary config={config} contactInfo={contactInfo} setContactInfo={setContactInfo} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] pt-24 sm:pt-32 pb-12 sm:pb-20">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-6xl">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-center mb-8 sm:mb-12"
        >
          <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4">
            Weboldal konfigurátor
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl text-white mb-2">
            Tervezd meg az{" "}
            <span className="italic text-gold-gradient">álomoldalad</span>
          </h1>
        </motion.div>

        {/* Progress */}
        <ProgressBar currentStep={currentStep} totalSteps={STEPS.length} />

        {/* Step Header */}
        <motion.div
          key={`header-${currentStep}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-10"
        >
          <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl text-white mb-1 sm:mb-2">
            {STEPS[currentStep - 1].title}
          </h2>
          <p className="text-zinc-500 text-sm sm:text-base">{STEPS[currentStep - 1].subtitle}</p>
        </motion.div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="mb-8 sm:mb-12"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 sm:pt-8 border-t border-white/10 gap-4">
          <Button
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="btn-gold-outline disabled:opacity-30 disabled:cursor-not-allowed flex-1 sm:flex-none"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Vissza</span>
          </Button>

          {currentStep < STEPS.length ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="btn-gold disabled:opacity-30 disabled:cursor-not-allowed flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Következő</span>
              <span className="sm:hidden">Tovább</span>
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="btn-gold disabled:opacity-30 disabled:cursor-not-allowed flex-1 sm:flex-none"
            >
              {isSubmitting ? "Küldés..." : "Elküldöm"}
              <Send className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
